'use client';
/* eslint-disable @next/next/no-img-element -- Article media uses API-provided URLs and the source design's intrinsic image sizing. */
import Link from 'next/link';
import type { ArticleDetails } from '@/app/shared/blog/types';
import { SubPageShell } from '@/app/shared/landing/pages/components/sub-page-sections';
import { formatArticleContent } from './article-content';
import { ArticleLikeButton } from './article-like-button';
import { Tooltip } from 'rizzui/tooltip';

function formatPublishedDate(value: string) {
  const dottedDate = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  const date = dottedDate
    ? new Date(
        Date.UTC(
          Number(dottedDate[3]),
          Number(dottedDate[2]) - 1,
          Number(dottedDate[1])
        )
      )
    : new Date(value.includes('T') ? value : value.replace(' ', 'T'));

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function ArticleBody({ content }: { content: string }) {
  return (
    <div
      className="!text-[clamp(1.0625rem,1.4vw,1.1875rem)] !leading-[1.75] !text-[#344256] [overflow-wrap:anywhere] dark:!text-[#c5d3e0] [&>:last-child]:!mb-0 [&_.ql-align-center]:!text-center [&_.ql-align-justify]:!text-justify [&_.ql-align-right]:!text-right [&_.ql-indent-1]:!ml-6 [&_.ql-indent-2]:!ml-12 [&_.ql-indent-3]:!ml-[4.5rem] [&_.ql-indent-4]:!ml-24 [&_.ql-indent-5]:!ml-24 [&_.ql-indent-6]:!ml-24 [&_.ql-indent-7]:!ml-24 [&_.ql-indent-8]:!ml-24 [&_.ql-indent-9]:!ml-24 [&_a]:!text-[#0183d0] [&_a]:!underline [&_a]:![text-underline-offset:0.18em] [&_blockquote]:!mb-4 [&_blockquote]:!border-l-4 [&_blockquote]:!border-[#03a1fe] [&_blockquote]:!pl-4 [&_blockquote]:!italic [&_h1]:!mb-3 [&_h1]:!mt-7 [&_h1]:!text-[clamp(2rem,4vw,3rem)] [&_h1]:!font-semibold [&_h1]:!leading-tight [&_h1]:!text-inherit [&_h2]:!mb-3 [&_h2]:!mt-7 [&_h2]:!text-[clamp(1.65rem,3vw,2.25rem)] [&_h2]:!font-semibold [&_h2]:!leading-tight [&_h2]:!text-inherit [&_h3]:!mb-3 [&_h3]:!mt-7 [&_h3]:!text-[clamp(1.35rem,2.5vw,1.75rem)] [&_h3]:!font-semibold [&_h3]:!leading-tight [&_h3]:!text-inherit [&_h4]:!mb-3 [&_h4]:!mt-7 [&_h4]:!font-semibold [&_h4]:!leading-tight [&_h4]:!text-inherit [&_h5]:!mb-3 [&_h5]:!mt-7 [&_h5]:!font-semibold [&_h5]:!leading-tight [&_h5]:!text-inherit [&_h6]:!mb-3 [&_h6]:!mt-7 [&_h6]:!font-semibold [&_h6]:!leading-tight [&_h6]:!text-inherit [&_li[data-list='bullet']]:!list-disc [&_li[data-list='ordered']]:!list-decimal [&_ol]:!mb-4 [&_ol]:!list-decimal [&_ol]:!pl-6 [&_p]:!mb-4 [&_pre]:!mb-4 [&_pre]:!overflow-x-auto [&_pre]:!rounded-xl [&_pre]:!bg-[#07182f] [&_pre]:!p-4 [&_pre]:!text-white [&_ul]:!mb-4 [&_ul]:!list-disc [&_ul]:!pl-6"
      dangerouslySetInnerHTML={{ __html: formatArticleContent(content) }}
    />
  );
}

export function ArticlePage({
  locale,
  article,
}: {
  locale: string;
  article: ArticleDetails;
}) {
  const publishedAt = article.published_at || article.created_at;
  const authorName = article.author.full_name.trim();

  return (
    <SubPageShell rootClassName="sub-page-root article-detail-root">
      <article className="!overflow-hidden !bg-white !pb-[clamp(5rem,8vw,7.5rem)] !pt-[clamp(5.25rem,5.5vw,6.25rem)] !text-[#07182f] dark:!bg-[#07182f] dark:!text-[#f4f8fc] max-[767px]:!pb-[4.5rem] max-[767px]:!pt-20">
        <div className="container">
          <header className="!mx-auto">
            <Link
              className="!mt-[clamp(0.75rem,1.5vw,1.25rem)] !inline-flex !items-center !gap-[0.55rem] !text-[0.8125rem] !font-bold !uppercase !tracking-[0.08em] !text-[#526174] !transition-[color,transform] !duration-[180ms] !ease-[ease] hover:!-translate-x-[3px] hover:!text-[#087ac0] focus-visible:!rounded focus-visible:!outline focus-visible:!outline-[3px] focus-visible:!outline-offset-4 focus-visible:!outline-[#087ac038] dark:!text-[#a8c3dc] dark:hover:!text-[#58b7ff]"
              href={`/${locale}/company/news`}
            >
              <span aria-hidden="true">←</span>
              Back to news
            </Link>

            <div className="!mt-[clamp(1.5rem,2.5vw,2.25rem)] !grid !grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)] !items-start !gap-x-[clamp(3rem,7vw,7rem)] !gap-y-8 max-[991px]:!grid-cols-1 max-[991px]:!gap-x-8 max-[991px]:!gap-y-6 max-[767px]:!mt-6">
              <div className="!min-w-0">
                <div className="!flex !flex-wrap !gap-2">
                  {article.is_featured ? (
                    <span className="!inline-flex !min-h-8 !items-center !rounded-lg !bg-[#07182f] !px-3 !py-[0.4rem] !text-xs !font-bold !uppercase !tracking-[0.07em] !text-white">
                      Featured
                    </span>
                  ) : null}
                  {article.category?.label ? (
                    <span className="!inline-flex !min-h-8 !items-center !rounded-lg !bg-[#edf7ff] !px-3 !py-[0.4rem] !text-xs !font-bold !uppercase !tracking-[0.07em] !text-[#087ac0] dark:!bg-[#0b2947] dark:!text-[#58b7ff]">
                      {article.category.label}
                    </span>
                  ) : null}
                </div>

                <h1 className="!mt-4 !max-w-[68rem] !break-words !text-[clamp(3rem,7vw,6.75rem)] !font-semibold !leading-[0.94] !tracking-[-0.06em] !text-[#07182f] [text-wrap:balance] dark:!text-[#f4f8fc] max-[767px]:!text-[clamp(2.5rem,13vw,4.25rem)] max-[767px]:!leading-[0.98]">
                  {article.title}
                </h1>
                <p className="!mt-[clamp(1rem,1.75vw,1.5rem)] !max-w-3xl !text-[clamp(1.25rem,2.1vw,1.75rem)] !leading-[1.45] !tracking-[-0.025em] !text-[#526174] [text-wrap:pretty] dark:!text-[#c5d3e0]">
                  {article.summary}
                </p>
              </div>

              <div className="!grid !grid-cols-1 !items-start !gap-4 !rounded-[0.875rem] !border !border-[#dce4ec] !bg-[#f5f8fb] !px-[1.125rem] !py-4 dark:!border-[rgba(171,202,229,0.2)] dark:!bg-[#0b1c3b] max-[991px]:!max-w-xl max-[767px]:!justify-items-start">
                <div className="!flex !min-w-0 !items-center !gap-[clamp(1rem,2.5vw,2rem)] max-[767px]:!flex-col max-[767px]:!items-start max-[767px]:!gap-[0.875rem] [&>*+*]:!border-l [&>*+*]:!border-[#dce4ec] [&>*+*]:!pl-[clamp(1rem,2.5vw,2rem)] dark:[&>*+*]:!border-[rgba(171,202,229,0.2)] max-[767px]:[&>*+*]:!border-l-0 max-[767px]:[&>*+*]:!border-t max-[767px]:[&>*+*]:!pl-0 max-[767px]:[&>*+*]:!pt-[0.875rem]">
                  <div className="!grid !min-w-0 !gap-1">
                    <Tooltip content={authorName ?? 'Published on'}>
                      <span className="!overflow-hidden !text-ellipsis !whitespace-nowrap !text-[0.625rem] !font-bold !uppercase !tracking-[0.09em] !text-[#68778a] dark:!text-[#a8c3dc]">
                        {authorName
                          ? `Written by ${authorName}`
                          : 'Published on'}
                      </span>
                    </Tooltip>
                    <time
                      className="!text-[0.9375rem] !font-bold !tabular-nums !text-[#07182f] dark:!text-[#f4f8fc]"
                      dateTime={publishedAt}
                    >
                      {formatPublishedDate(publishedAt)}
                    </time>
                  </div>
                  <div className="!grid !min-w-32 !gap-1">
                    <span className="!overflow-hidden !text-ellipsis !whitespace-nowrap !text-[0.625rem] !font-bold !uppercase !tracking-[0.09em] !text-[#68778a] dark:!text-[#a8c3dc]">
                      Article views
                    </span>
                    <span className="!whitespace-nowrap !text-[0.9375rem] !font-bold !tabular-nums !text-[#07182f] dark:!text-[#f4f8fc]">
                      {article.views_count.toLocaleString('en-US')}{' '}
                      {article.views_count === 1 ? 'view' : 'views'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="!mb-0 !mt-4 !flex !flex-wrap !items-center !gap-[0.875rem] max-[767px]:!flex-row max-[767px]:!gap-2">
                <span className="!text-[0.8125rem] !font-semibold !text-[#526174] dark:!text-[#c5d3e0]">
                  Was this article helpful?
                </span>
                <ArticleLikeButton
                  articleSlug={article.slug}
                  initialLikes={article.likes_count}
                  buttonClassName="min-h-[2.625rem] min-w-[8.5rem] justify-center !border-[#b8e1fa] !bg-[#0b2947] !text-[#087ac0] transition-[background,border-color,box-shadow,transform] duration-[180ms] ease-[ease] enabled:hover:-translate-y-px enabled:hover:!border-[#8fd2f8] enabled:hover:!bg-[#dff2ff] enabled:hover:!text-[#056da9] enabled:active:translate-y-0 enabled:active:scale-[0.98] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#03a1fe47] focus-visible:outline-offset-[3px] focus-visible:shadow-[0_0_0_5px_rgba(3,161,254,0.1)] dark:!border-[rgba(88,183,255,0.42)] dark:!bg-[#0b2947] dark:!text-[#8bcfff] dark:enabled:hover:!border-[rgba(88,183,255,0.42)] dark:enabled:hover:!bg-[#0b2947] dark:enabled:hover:!text-[#8bcfff]"
                />
              </div>
            </div>

            {article.cover?.url ? (
              <figure className="!mx-auto !mb-0 !mt-[clamp(1.5rem,2vw,2rem)] !h-[clamp(18rem,36vw,32rem)] !max-h-[32rem] !w-full !max-w-full !overflow-hidden !rounded-[clamp(1rem,2vw,1.75rem)] !bg-[#e8eef3] !shadow-[0_24px_70px_rgba(7,24,47,0.12)] max-[767px]:!h-[clamp(18rem,72vw,28rem)] max-[767px]:!max-h-[28rem]">
                <img
                  alt={article.title}
                  className="!block !h-full !max-h-full !w-full !max-w-full !object-cover"
                  loading="eager"
                  src={article.cover.url}
                />
              </figure>
            ) : null}
          </header>

          <div className="!mx-auto !w-full !max-w-full !pt-[clamp(2rem,3.5vw,3.5rem)]">
            <ArticleBody content={article.content} />
          </div>

          {article.gallery.length ? (
            <div className="!mt-[clamp(3.5rem,7vw,6.5rem)] !grid !grid-cols-2 !gap-4 max-[767px]:!grid-cols-1">
              {article.gallery.map((image) => (
                <figure
                  className="!h-[clamp(16rem,30vw,24rem)] !max-h-96 !w-full !min-w-0 !max-w-full !overflow-hidden !rounded-2xl !bg-[#e8eef3]"
                  key={image.id}
                >
                  <img
                    alt={`${article.title}, ${image.file_name}`}
                    className="!block !h-full !max-h-full !w-full !max-w-full !object-cover"
                    loading="lazy"
                    src={image.url}
                  />
                </figure>
              ))}
            </div>
          ) : null}
        </div>
      </article>
    </SubPageShell>
  );
}
